<script setup lang="ts">

const props = defineProps<{
  sections: VideoSection[]
  onSeek?: (timestamp: number) => void
  selectedIndex: number | null;
  playback: { currentTime: number };
}>()


const carousel = ref<HTMLElement | null>(null);
const selectedIndex = ref<number | null>(null);

const sortedSections = computed(() =>
    Array.isArray(props.sections)
        ? [...props.sections].sort((a, b) => a.start_ms - b.start_ms)
        : []
)

watch(() => props.selectedIndex, async (index) => {
  if (index === null) return;
  await nextTick();

  const carouselEl = carousel.value;
  const cardEls = carouselEl?.querySelectorAll('.wad4-video-toc-card');

  if (!carouselEl || !cardEls || !cardEls[index]) return;

  const cardEl = cardEls[index] as HTMLElement;
  const carouselRect = carouselEl.getBoundingClientRect();
  const cardRect = cardEl.getBoundingClientRect();

  const scrollOffset =
      carouselEl.scrollLeft +
      cardRect.left -
      carouselRect.left -
      carouselRect.width / 2 +
      cardRect.width / 2;

  carouselEl.scrollTo({ left: scrollOffset, behavior: 'smooth' });
});
function handleSeek(timestamp: number, index: number) {
  selectedIndex.value = index;
  props.onSeek?.(timestamp, index);
}
function scrollLeft() {
  carousel.value?.scrollBy({left: -300, behavior: 'smooth'})
}

function scrollRight() {
  carousel.value?.scrollBy({left: 300, behavior: 'smooth'})
}
</script>

<template>
  <div class="wad4-video-toc">
    <div ref="carousel" class="wad4-video-toc__track">
      <VideoTocCard class="wad4-video-toc__card"
          v-for="(section, index) in sortedSections"
          :key="section.id"
          :section="section"
          :index="index"
          :onSeek="handleSeek"
          :selected="index === props.selectedIndex"
          :currentTime="props.playback.currentTime"
      />
    </div>
    <div class="wad4-video-toc__controls">
      <ButtonIcon @pointerup="scrollLeft">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.77 3.77L16 2L6 12L16 22L17.77 20.23L9.54 12L17.77 3.77Z" fill="currentColor"/>
        </svg>
      </ButtonIcon>
      <ButtonIcon @pointerup="scrollRight">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
          <path d="M6.23047 20.8296L8.00047 22.5996L18.0005 12.5996L8.00047 2.59961L6.23047 4.36961L14.4605 12.5996L6.23047 20.8296Z" fill="currentColor"/>
        </svg>
      </ButtonIcon>
    </div>
  </div>
</template>

<style lang="scss">

.wad4-video-toc {
  overflow: hidden;
  width: 100%;

  &__track {
    display: flex;
    gap: $wad4-spacing-3;
    scroll-snap-type: x mandatory;
    overflow-x: auto;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
    -ms-overflow-style: none;
    scrollbar-width: none;
    padding: $wad4-spacing-lg;
    margin-top: $wad4-spacing-xl;
    background: linear-gradient(180deg, $wad4-c-background-bg-primary 0.13%, $wad4-c-background-bg-secondary 39.07%);
    border-radius: 8px;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__card {
    --wad4-video-toc-cards: 1;
    --wad4-video-toc-gaps: 1;
    --wad4-video-toc-card-partial: 0.10;

    scroll-snap-align: center;
    flex: 0 0 calc((100% - ($wad4-spacing-3 * var(--wad4-video-toc-gaps))) / (var(--wad4-video-toc-cards) + var(--wad4-video-toc-card-partial)));

    @include mqBreakPoint(480px) {
      --wad4-video-toc-cards: 2;
      --wad4-video-toc-gaps: 2;
    }

    @include mqBreakPoint(768px) {
      --wad4-video-toc-cards: 3;
      --wad4-video-toc-gaps: 3;
      --wad4-video-toc-card-partial: 0.25;
    }

    @include mqBreakPoint($wad4-breakpoint-desktop-min) {
      --wad4-video-toc-cards: 2;
      --wad4-video-toc-gaps: 2;
    }

    @include mqBreakPoint(1100px) {
      --wad4-video-toc-cards: 3;
      --wad4-video-toc-gaps: 3;
    }

    @include mqBreakPoint(1350px) {
      --wad4-video-toc-cards: 4;
      --wad4-video-toc-gaps: 4;
      --wad4-video-toc-card-partial: 0.35;
    }
  }

  &__controls {
    display: flex;
    justify-content: flex-start;
    gap: $wad4-spacing-xs;
    align-items: center;
    flex: 1 0 0;
    margin-top: $wad4-spacing-md;
  }

}

</style>
